import { checkIfUsernameExists } from "../modules/registration/slices/register.actions";
import { trackEvent } from "../utils/tracking";
import { TRACKING_EVENTS } from "../utils/trackingDefinitions";

export const RULES = Object.freeze({
	REGISTRATION_FIELDS_LENGTH: {
		ADDRESS: 60,
		CITY: 40,
		NAME: 40,
		EMAIL: 70,
		PASSWORD_MIN: 8,
		PASSWORD_MAX: 50,
	},
});

export const getFieldsConfiguration = ({
	dispatch,
	register,
	errors,
	values,
	options,
}) =>
	Object.freeze({
		PHONE: {
			FIELD: register("phone", {
				value: values.phone,
				required: "komid.register.validation.required",
				validate: {
					rule: (value) => {
						if (!new RegExp(values.validationRules.mobilePhone).test(value)) {
							return "komid.register.validation.phone.invalid";
						}
						return null;
					},
				},
			}),
			ERROR: errors?.phone?.message,
		},
		NAME: {
			FIELD: register("name", {
				value: values.name,
				...(options.isActive && {
					required: "komid.register.validation.required",
					maxLength: {
						value: RULES.REGISTRATION_FIELDS_LENGTH.NAME,
						message: "komid.register.validation.required",
					},
					validate: {
						twoStrings: (value) => {
							if (value.trim().indexOf(" ") === -1) {
								return "komid.register.validation.required";
							}
							return null;
						},
						notEmpty: (value) => {
							if (value.trim() === "") {
								return "komid.register.validation.required";
							}
							return null;
						},
					},
				}),
			}),
			ERROR: errors?.name?.message,
		},
		ADDRESS: {
			FIELD: register("addressLine1", {
				value: values.addressLine1,
				...(options.isActive && {
					required: "komid.register.validation.required",
					validate: {
						maxLength: (value) => {
							if (
								value.trim().length > RULES.REGISTRATION_FIELDS_LENGTH.ADDRESS
							) {
								return "komid.register.validation.length.max";
							}
							return null;
						},
						notEmpty: (value) => {
							if (value.trim() === "") {
								return "komid.register.validation.required";
							}
							return null;
						},
					},
				}),
			}),
			ERROR: errors?.addressLine1?.message,
		},
		POSTCODE: {
			FIELD: register("postcode", {
				value: values.postcode,
				...(options.isActive && {
					required: "komid.register.validation.required",
					validate: {
						notEmpty: (value) => {
							if (value.trim() === "") {
								return "komid.register.validation.required";
							}
							return null;
						},
					},
				}),
			}),
			ERROR: errors?.postcode?.message,
		},
		CITY: {
			FIELD: register("city", {
				value: values.city,
				...(options.isActive && {
					required: "komid.register.validation.required",
					validate: {
						notEmpty: (value) => {
							if (value.trim() === "") {
								return "komid.register.validation.required";
							}
							return null;
						},
					},
				}),
			}),
			ERROR: errors?.city?.message,
		},
		EMAIL: {
			FIELD: register("email", {
				value: values.email,
				...(options.isActive && {
					required: "komid.register.validation.required",
					maxLength: {
						value: RULES.REGISTRATION_FIELDS_LENGTH.EMAIL,
						message: "komid.register.validation.format.invalid",
					},
					validate: {
						rule: (value) => {
							if (!new RegExp(values.validationRules.email).test(value)) {
								return "komid.register.validation.format.invalid";
							}
							return null;
						},
						exists: async (value) => {
							if (
								options.isActive &&
								!options.isFetching &&
								!options.isCheckingUsername &&
								options.savedUsername !== value
							) {
								const response = await dispatch(
									checkIfUsernameExists({ username: value, form: "b2c" }),
								).unwrap();

								if (response.result.isSuccess && response.result.userExists) {
									trackEvent({
										event: TRACKING_EVENTS.COMMON.EVENT.USER_INTERACTION,
										target: TRACKING_EVENTS.REGISTRATION.APP,
										key: "action",
										value: TRACKING_EVENTS.COMMON.EVENT.CHECK_USERNAME_EXISTS,
									});

									return "komid.register.validation.username.exists";
								} else {
									return null;
								}
							}

							return null;
						},
					},
				}),
			}),
			ERROR: errors?.email?.message,
		},
		PASSWORD: {
			FIELD: register("password", {
				value: values.password,
				...(options.isActive && {
					required: "komid.register.validation.required",
					minLength: {
						value: RULES.REGISTRATION_FIELDS_LENGTH.PASSWORD_MIN,
						message: "komid.register.validation.length.min",
					},
					validate: {
						notEmpty: (value) => {
							if (value.trim() === "") {
								return "komid.register.validation.required";
							}
							return null;
						},
					},
				}),
			}),
			ERROR: errors?.password?.message,
		},
		ACCEPT_WEEKLY_NEWSLETTER: {
			FIELD: register("acceptWeeklyNewsletter", {
				value: values.acceptWeeklyNewsletter,
			}),
			ERROR: errors?.acceptWeeklyNewsletter?.message,
		},
		ACCEPT_CUSTOMER_CONDITIONS: {
			FIELD: register("acceptCustomerConditions", {
				value: values.acceptCustomerConditions,
				...(options.isActive &&
					!options.useConsents &&
					!options.isPrivateConsentsEnabled && {
						required: "komid.register.validation.required",
					}),
			}),
			ERROR: errors?.acceptCustomerConditions?.message,
		},
		ACCEPT_SALES_CONDITIONS: {
			FIELD: register("acceptSalesConditions", {
				value: values.acceptSalesConditions,
				...(options.isActive &&
					options.useConsents && {
						required: "komid.register.validation.required",
					}),
			}),
			ERROR: errors?.acceptSalesConditions?.message,
		},
		ACCEPT_EMAIL_NOTIFICATIONS: {
			FIELD: register("acceptEmailNotifications", {
				value: values.acceptEmailNotifications,
				...(options.isActive &&
					options.useConsents && {
						validate: {
							notEmpty: (value) => {
								if (value === null) {
									return "komid.register.validation.required";
								}
								return null;
							},
						},
					}),
			}),
			ERROR: errors?.acceptEmailNotifications?.message,
		},
		ACCEPT_SMS_NOTIFICATIONS: {
			FIELD: register("acceptSMSNotifications", {
				value: values.acceptSMSNotifications,
				...(options.isActive &&
					options.useConsents && {
						validate: {
							notEmpty: (value) => {
								if (value === null) {
									return "komid.register.validation.required";
								}
								return null;
							},
						},
					}),
			}),
			ERROR: errors?.acceptSMSNotifications?.message,
		},
	});
